'use client';

import { useTranslation } from '@/app/i18n/client';
import { useAuthV2 } from '@/hooks/useAuthV2';
import { cn } from '@/lib/utils';
import { COUNTRIES } from '@/utils/constants';
import { formatRetryTime, formatSeconds } from '@/utils/date';
import { clearPendingAuth, getPendingAuth, setPendingAuth } from '@/utils/pending-auth';
import {
  getSessionStorageWithoutParse,
  removeSessionStorage,
  setSessionStorageWithoutParse,
} from '@/utils/session-storage';
import { replaceWithAsterisks } from '@/utils/string';
import { AuthMethod } from '@/utils/types';
import { AnimatePresence, motion } from 'framer-motion';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ArrowLeft, ChevronDown, GitMerge, Mail, Smartphone, Ticket } from 'lucide-react';
import { useParams, useRouter } from 'next/navigation';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { GoogleReCaptchaV2 } from '../GoogleReCaptchaV2';
import { MaskInput } from '../maskInput';
import Modal from '../modal';
import { Button } from '../ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useToast } from '../ui/use-toast';
import VerificationInput from '../ui/verificationInput';

const WHATSAPP_SVG_PATH = [
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164',
  '-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059',
  '-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52',
  '-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01',
  '-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074',
  '.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118',
  '.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347',
  'm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374',
  'a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898',
  'a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884',
  'm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945',
  'L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893',
  'a11.821 11.821 0 00-3.48-8.413z',
].join('');

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox='0 0 24 24' fill='currentColor' xmlns='http://www.w3.org/2000/svg'>
    <path d={WHATSAPP_SVG_PATH} />
  </svg>
);

interface LoginDialogProps {
  loginOpen: boolean;
  setLoginOpen: Dispatch<SetStateAction<boolean>>;
  defaultPinStep?: boolean;
  phoneNumber?: string;
  emailAddress?: string;
  redirectAfterSignIn?: string;
  authenticateUsers?: 'v1' | 'v2';
  isMFALogin?: boolean;
  defaultAuthMethod?: AuthMethod;
  dependentCode?: string;
  sentToEmail?: string;
  isNewUserFromPage?: boolean;
}

interface LoginFormInput {
  phone: string;
  email: string;
}

// Easing suave para animações rápidas
const smoothEase: [number, number, number, number] = [0.4, 0, 0.2, 1];

// Valida se session é uma string válida (não objeto vazio)
const isValidSession = (session: any): session is string => {
  return typeof session === 'string' && session.length > 0;
};

// Pulsing dots loader component
const PulsingDots = () => (
  <div className='flex items-center gap-1'>
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className='h-1.5 w-1.5 bg-zinc-400'
        animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
        transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
      />
    ))}
  </div>
);

export const LoginDialog = ({
  loginOpen,
  setLoginOpen,
  defaultPinStep = false,
  phoneNumber = '',
  emailAddress = '',
  isMFALogin = false,
  redirectAfterSignIn = '/checkout',
  authenticateUsers,
  defaultAuthMethod = 'whatsapp',
  dependentCode: dependentCodeProp,
  sentToEmail: sentToEmailProp,
  isNewUserFromPage = false,
}: LoginDialogProps) => {
  const { lng } = useParams() as { lng: never };
  const { t } = useTranslation(lng, ['login', 'errors']);
  const { loginWithEmail, whatsappLogin, smsLogin = true } = useFlags();

  const [pinStep, setPinStep] = useState(defaultPinStep);
  const [authMethod, setAuthMethod] = useState<AuthMethod>('sms');
  const [smsFailedSuggestion, setSmsFailedSuggestion] = useState(false);
  const [authErrors, setAuthErrors] = useState<{
    sms?: string;
    email?: string;
    whatsapp?: string;
    dependent_code?: string;
  }>({});
  const [lastSuccessfulMethod, setLastSuccessfulMethod] = useState<AuthMethod | null>(null);
  const { handleSubmit, setValue, watch, control } = useForm<LoginFormInput>({
    defaultValues: { phone: '', email: '' },
  });

  const [code, setCode] = useState<string>('');
  const [counter, setCounter] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);

  // Bridge Verification state
  const [bridgeStep, setBridgeStep] = useState<'idle' | 'ask' | 'phone-entry' | 'code-verification'>('idle');
  const [bridgeToken, setBridgeToken] = useState<string | null>(null);
  const [bridgeMaskedPhone, setBridgeMaskedPhone] = useState<string | null>(null);
  const [bridgeEmail, setBridgeEmail] = useState<string>('');
  const [bridgeSending, setBridgeSending] = useState(false);
  const [bridgeVerificationMethod, setBridgeVerificationMethod] = useState<'whatsapp' | 'sms'>('whatsapp');
  const [bridgeCode, setBridgeCode] = useState<string>('');
  const [bridgeCounter, setBridgeCounter] = useState(0);
  const [bridgeBypass, setBridgeBypass] = useState(false);

  // Merged Account Redirect state
  const [mergedAccountInfo, setMergedAccountInfo] = useState<{
    redirectMethod: string;
    user: { id: string; name: string; email: string; phone: string };
  } | null>(null);

  const router = useRouter();
  const { loadUser, setUserDirectly } = useAuthV2();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [isDependentMode, setIsDependentMode] = useState(false);
  const [dependentCodeInput, setDependentCodeInput] = useState('');
  const [dependentSentToEmail, setDependentSentToEmail] = useState('');
  const {
    resendSignInCodeV2,
    handleSignIn,
    handleSignUp,
    handleSignUpConfirmation,
    handleConfirmSignIn,
    sendEmailVerificationCode,
    verifyEmailCode,
    sendWhatsAppCode,
    verifyWhatsAppCode,
    resendWhatsAppCode,
    sendDependentCode,
    verifyDependentCode,
    isValidEmail,
    isValidPhone,
    getAuthErrorMessage,
    isSMSDefenseError,
    isRateLimitError,
    isForeignDDI,
    sendBridgeVerification,
    confirmBridgeVerification,
  } = useAuthV2();

  const brazilIndex = COUNTRIES.findIndex((country) => ['Brazil', 'Brasil'].includes(country.name[lng || 'pt-BR']));
  const [selectedCountry, setSelectedCountry] = useState(brazilIndex);

  const DDI = COUNTRIES[selectedCountry]?.mobileCode;
  const COUNTRY = COUNTRIES[selectedCountry]?.name[lng || 'pt-BR'];

  const phone = watch('phone');
  const email = watch('email');

  const availableAuthMethods: AuthMethod[] = [
    ...(smsLogin ? ['sms' as AuthMethod] : []),
    ...(whatsappLogin ? ['whatsapp' as AuthMethod] : []),
    ...(loginWithEmail ? ['email' as AuthMethod] : []),
  ];

  useEffect(() => {
    setValue('phone', phoneNumber);
  }, [phoneNumber, setValue]);

  useEffect(() => {
    setValue('email', emailAddress);
  }, [emailAddress]);

  useEffect(() => {
    if (!pinStep) {
      setBridgeBypass(false);
    }
  }, [email, pinStep]);

  useEffect(() => {
    setPinStep(defaultPinStep);
    setCode('');
    setCounter(0);
    setSendingCode(false);
    setValidatingCode(false);

    // Handle dependent_code mode from props
    if (defaultAuthMethod === 'dependent_code' && dependentCodeProp) {
      setAuthMethod('dependent_code');
      setIsDependentMode(true);
      setDependentCodeInput(dependentCodeProp);
      setDependentSentToEmail(sentToEmailProp || '');
      setSmsFailedSuggestion(false);
      setAuthErrors({});
      return;
    }

    setIsDependentMode(false);
    setDependentCodeInput('');
    setDependentSentToEmail('');

    // When opening from page (defaultPinStep === true), always respect defaultAuthMethod
    // When opening from dialog itself, use lastSuccessfulMethod or defaultAuthMethod
    let method: AuthMethod = defaultAuthMethod;

    const isDefaultMethodAvailable =
      (defaultAuthMethod === 'sms' && smsLogin) ||
      (defaultAuthMethod === 'email' && loginWithEmail) ||
      (defaultAuthMethod === 'whatsapp' && whatsappLogin);

    if (defaultPinStep) {
      // Opening from page - always use defaultAuthMethod if available, otherwise fallback
      if (isDefaultMethodAvailable) {
        method = defaultAuthMethod;
      } else {
        // Fallback to first available method
        if (whatsappLogin) method = 'whatsapp';
        else if (loginWithEmail) method = 'email';
        else if (smsLogin) method = 'sms';
      }
    } else {
      // Opening from dialog itself - prioritize lastSuccessfulMethod, then defaultAuthMethod
      if (lastSuccessfulMethod) {
        const isLastMethodAvailable =
          (lastSuccessfulMethod === 'sms' && smsLogin) ||
          (lastSuccessfulMethod === 'email' && loginWithEmail) ||
          (lastSuccessfulMethod === 'whatsapp' && whatsappLogin);

        if (isLastMethodAvailable) {
          method = lastSuccessfulMethod;
        } else if (isDefaultMethodAvailable) {
          method = defaultAuthMethod;
        } else {
          // Fallback to first available method
          if (whatsappLogin) method = 'whatsapp';
          else if (loginWithEmail) method = 'email';
          else if (smsLogin) method = 'sms';
        }
      } else if (isDefaultMethodAvailable) {
        method = defaultAuthMethod;
      } else {
        // Fallback to first available method
        if (whatsappLogin) method = 'whatsapp';
        else if (loginWithEmail) method = 'email';
        else if (smsLogin) method = 'sms';
      }
    }
    setAuthMethod(method);
    setSmsFailedSuggestion(false);
    setAuthErrors({});
    // Reset bridge and merged state
    setBridgeToken(null);
    setBridgeMaskedPhone(null);
    setBridgeSending(false);
    setBridgeCode('');
    setBridgeCounter(0);
    setBridgeBypass(false);
    setMergedAccountInfo(null);

    // If the page already called send-code and got isNewUser: true, start at bridge ask step
    if (loginOpen && isNewUserFromPage && emailAddress) {
      setBridgeStep('ask');
      setBridgeEmail(emailAddress);
      setCounter(60);
      setPinStep(false);
    } else {
      setBridgeStep('idle');
      setBridgeEmail('');
    }
  }, [
    loginOpen,
    lastSuccessfulMethod,
    loginWithEmail,
    whatsappLogin,
    smsLogin,
    defaultAuthMethod,
    defaultPinStep,
    isNewUserFromPage,
    emailAddress,
    dependentCodeProp,
    sentToEmailProp,
  ]);

  useEffect(() => {
    if (counter > 0) {
      setTimeout(() => {
        setCounter(counter - 1);
      }, 1000);
    }
  }, [counter]);

  useEffect(() => {
    if (bridgeCounter > 0) {
      setTimeout(() => {
        setBridgeCounter(bridgeCounter - 1);
      }, 1000);
    }
  }, [bridgeCounter]);

  useEffect(() => {
    const pending = getPendingAuth();
    if (!pending) return;

    if (pending.method === 'email') {
      setValue('email', pending.identifier);
    } else if (pending.method === 'sms' || pending.method === 'whatsapp') {
      const local = pending.identifier.replace(/^\+\d{1,3}/, '');
      setValue('phone', local);
    }
    setAuthMethod(pending.method);
    setPinStep(true);
    setSessionStorageWithoutParse('mfa_session', pending.session);

    const elapsedSec = Math.floor((Date.now() - pending.startedAt) / 1000);
    setCounter(Math.max(0, 60 - elapsedSec));
    setLoginOpen(true);
  }, []);

  const clearPhone = (phone: string) => {
    return phone.replace(/\D/g, '');
  };

  const formatPhoneForMask = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '').replace(/^55/, '');

    if (cleanPhone.length === 11) {
      return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleanPhone.length === 10) {
      return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }

    return cleanPhone;
  };

  const handleSignInResponse = async (response: any) => {
    if (response?.user?.id) {
      showToast({ title: t(`errors:errorSigningInTitle`), description: t('errors:errorSigningInDescription') });
    } else {
      // Retry com backoff para mobile - aguarda propagação do cookie
      const maxRetries = 3;
      let user = null;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 200 * attempt));
        }
        user = await loadUser({ preserveTokensOnError: true });
        if (user) break;
      }

      if (user) {
        clearPendingAuth();
        setLoginOpen(false);
        setTimeout(() => {
          router.push(redirectAfterSignIn);
        }, 100);
      } else {
        showToast({
          title: t('authenticationFailed'),
          description: t('tryAgainLater'),
          variant: 'error',
        });
        clearPendingAuth();
        setPinStep(false);
        setCode('');
      }
    }
  };

  const handleSuccessfulAuthentication = async (method: AuthMethod, userFromApi?: any) => {
    setLastSuccessfulMethod(method);
    setLoadingUserData(true);

    // SEMPRE chamar /me para obter dados completos do usuário
    // O userFromApi é usado apenas como fallback se /me falhar
    const maxRetries = 3;
    let user = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 200 * attempt));
      }
      user = await loadUser({ preserveTokensOnError: true });
      if (user) break;
    }

    // Se /me falhou mas temos userFromApi, usar como fallback
    if (!user && userFromApi) {
      setUserDirectly(userFromApi);
      user = userFromApi;
    }

    if (user) {
      clearPendingAuth();
      setLoginOpen(false);
      setTimeout(() => router.push(redirectAfterSignIn), 100);
    } else {
      setLoadingUserData(false);
      showToast({
        title: t('authenticationFailed'),
        description: t('tryAgainLater'),
        variant: 'error',
      });
      clearPendingAuth();
      setPinStep(false);
      setCode('');
    }
  };

  const handleAuthenticationError = (method: AuthMethod, error: any) => {
    // Handle nested error structure: { error: { code, retryAfter } }
    const errorData = error?.error || error;
    const errorCode =
      typeof errorData?.code === 'string' ? parseInt(errorData.code, 10) : errorData?.code || error?.code;
    const retryAfter =
      errorData?.retryAfter ||
      errorData?.payload?.remainingSeconds ||
      errorData?.remainingSeconds ||
      error?.retryAfter ||
      error?.payload?.remainingSeconds ||
      error?.remainingSeconds;
    const suggestion = errorData?.suggestion || error?.suggestion;
    const alternativeMethod = suggestion?.alternativeMethod || errorData?.alternativeMethod || error?.alternativeMethod;

    // Defense service errors (SMS and WhatsApp share the same delivery defense, e.g. IP velocity 400801).
    // Email auth does not go through it, so it is a real fallback for both methods.
    if ((method === 'sms' || method === 'whatsapp') && isSMSDefenseError(errorCode)) {
      const shouldSuggestEmail = error?.shouldSuggestEmailAuth === true || alternativeMethod === 'email';
      const unavailableTitle = method === 'whatsapp' ? t('errors:whatsappUnavailable') : t('errors:smsUnavailable');

      const errorMessage = t(`errors:${errorCode}`) || error?.message || unavailableTitle;
      let description = errorMessage;

      if (suggestion?.message) {
        description += ` ${suggestion.message}`;
      } else if (shouldSuggestEmail && loginWithEmail) {
        description += ` ${t('errors:suggestionUseEmail')}`;
      }

      showToast({
        title: unavailableTitle,
        description,
        variant: 'warning',
        duration: 7000,
      });

      // Auto-switch to email if feature is enabled and email auth is suggested
      if (shouldSuggestEmail && loginWithEmail) {
        setTimeout(() => {
          switchAuthMethod('email');
        }, 1500);
      }
      return;
    }

    if (isRateLimitError(errorCode) || errorCode === 400668 || errorCode === 400669 || errorCode === 400724) {
      const retrySeconds = retryAfter || 60;
      const retryTime = formatRetryTime(retrySeconds, lng as string);

      const title = t('errors:tooManyAttempts') || t('errors:errorTitle') || 'Muitas tentativas';
      let description = '';

      if (errorCode === 400806) {
        description =
          t('errors:400806', { retryTime }) || `Muitas requisições. Por favor, tente novamente em ${retryTime}.`;
      } else if (errorCode === 400668) {
        description =
          t('errors:400668', { remainingSeconds: retrySeconds }) ||
          `Muitas tentativas. Aguarde ${retrySeconds} segundo(s) antes de tentar novamente.`;
      } else if ([400669, 400724].includes(errorCode)) {
        description = t(`errors:${errorCode}`, { retryTime }) || `Muitas tentativas. Tente novamente em ${retryTime}.`;
      }

      if (suggestion?.message) {
        description += ` ${suggestion.message}`;
      }

      if (!description || description.trim() === '') {
        description = t('errors:anErrorOccurred') || 'Ocorreu um erro. Por favor, tente novamente.';
      }

      showToast({
        title: title || t('errors:errorTitle') || 'Erro',
        description: description || t('errors:anErrorOccurred') || 'Ocorreu um erro. Por favor, tente novamente.',
        variant: 'error',
        duration: 8000,
      });

      if (alternativeMethod && availableAuthMethods.includes(alternativeMethod as AuthMethod)) {
        setTimeout(() => {
          if (alternativeMethod === 'email' && loginWithEmail) {
            switchAuthMethod('email');
          } else if (alternativeMethod === 'whatsapp' && whatsappLogin) {
            switchAuthMethod('whatsapp');
          } else if (alternativeMethod === 'sms' && smsLogin) {
            switchAuthMethod('sms');
          }
        }, 2000);
      }
      return;
    }

    if (errorCode === 400621 || errorCode === 400630) {
      const errorMessage = t(`errors:${errorCode}`) || error?.message;
      let description = errorMessage;

      if (suggestion?.message) {
        description += ` ${suggestion.message}`;
      } else {
        description += ` ${t('errors:suggestionRequestNewCode')}`;
      }

      showToast({
        title: t('errors:verificationFailed'),
        description,
        variant: 'error',
        duration: 5000,
      });
      return;
    }

    if (errorCode === 400631) {
      const errorMessage = t(`errors:${errorCode}`) || error?.message;
      let description = errorMessage;

      if (suggestion?.message) {
        description += ` ${suggestion.message}`;
      }

      showToast({
        title: t('errors:verificationFailed'),
        description,
        variant: 'error',
        duration: 5000,
      });
      return;
    }

    if (errorCode === 400909 && method === 'email') {
      setBridgeEmail(email);
      setBridgeStep('phone-entry');
      return;
    }

    console.log('errorCode', errorCode);
    const codeErrors = [400660, 400661, 400806].includes(errorCode);
    if (codeErrors) {
      showToast({
        title: t('errors:errorTitle'),
        description: t(`errors:${errorCode}`),
        variant: 'error',
        duration: 4000,
      });
      return;
    }

    // Default error handling
    const authMethodForError = method === 'dependent_code' ? 'email' : method;
    const errorMessage =
      method === 'sms' && !pinStep ? t('smsDeliveryFailed') : getAuthErrorMessage(error, authMethodForError);

    showToast({
      title: pinStep ? t('errors:verificationFailed') : t('errors:errorTitle'),
      description: errorMessage,
      variant: 'error',
      duration: 4000,
    });
  };

  const confirm = async () => {
    if (!pinStep) {
      setSendingCode(true);
      try {
        if (authMethod === 'sms') {
          try {
            const signInResponse = await handleSignIn(`+${clearPhone(`+${DDI}${phone}`)}`);

            if (signInResponse?.notConfirmed) {
              setPinStep(true);
              const sessionValue = isValidSession(signInResponse?.session) ? signInResponse.session : '';
              setSessionStorageWithoutParse('mfa_session', sessionValue);
              setPendingAuth({ method: 'sms', identifier: `+${clearPhone(`+${DDI}${phone}`)}`, session: sessionValue });
              setCounter(60);
              return;
            }

            if (signInResponse?.token?.idToken) {
              await handleSuccessfulAuthentication('sms', signInResponse?.user);
              return;
            }

            if (signInResponse?.user?.id) {
              setPinStep(true);
              const sessionValue = isValidSession(signInResponse?.session) ? signInResponse.session : '';
              if (sessionValue) {
                setSessionStorageWithoutParse('mfa_session', sessionValue);
              }
              setPendingAuth({ method: 'sms', identifier: `+${clearPhone(`+${DDI}${phone}`)}`, session: sessionValue });
              setCounter(60);
              return;
            }
          } catch (signInError: any) {
            if (signInError?.code === 400671) {
              setPinStep(true);
              setPendingAuth({ method: 'sms', identifier: `+${clearPhone(`+${DDI}${phone}`)}`, session: '' });
              setCounter(60);
              return;
            }

            await handleSignUp(`+${clearPhone(`+${DDI}${phone}`)}`);
            removeSessionStorage('mfa_session');
            setPinStep(true);
            setPendingAuth({ method: 'sms', identifier: `+${clearPhone(`+${DDI}${phone}`)}`, session: '' });
            setCounter(60);
          }
        } else if (authMethod === 'email') {
          // Email authentication
          if (!isValidEmail(email)) {
            handleAuthenticationError('email', { message: t('pleaseEnterValidEmail') });
            setSendingCode(false);
            return;
          }

          const response = await sendEmailVerificationCode(email);
          console.log('[bridge-debug] send-code response:', response);
          if (response.success) {
            if (response.isNewUser) {
              setBridgeEmail(email);
              setBridgeStep('ask');
              setCounter(60);
            } else {
              setPinStep(true);
              setPendingAuth({ method: 'email', identifier: email, session: '' });
              setCounter(60);
            }
          } else {
            handleAuthenticationError('email', { message: response.message });
          }
        } else if (authMethod === 'whatsapp') {
          // WhatsApp authentication
          const fullPhone = `+${clearPhone(`+${DDI}${phone}`)}`;
          if (!isValidPhone(fullPhone)) {
            handleAuthenticationError('whatsapp', { message: t('pleaseEnterValidPhone') });
            setSendingCode(false);
            return;
          }

          const response = await sendWhatsAppCode(fullPhone);
          if (response) {
            setPinStep(true);
            setPendingAuth({ method: 'whatsapp', identifier: fullPhone, session: '' });
            setCounter(60);
          }
        } else if (authMethod === 'dependent_code') {
          // Dependent code authentication
          const codeToSend = dependentCodeInput.trim();
          if (!codeToSend) {
            showToast({ title: t('dependentCodeInvalid'), description: t('dependentCodePlaceholder') });
            setSendingCode(false);
            return;
          }

          const response = await sendDependentCode(codeToSend);
          if (response?.sentToEmail) {
            setDependentSentToEmail(response.sentToEmail);
            setPinStep(true);
            setPendingAuth({ method: 'dependent_code', identifier: codeToSend, session: '' });
            setCounter(60);
          }
        }
      } catch (error: any) {
        const errorData = error?.error || error;
        const retryAfter =
          errorData?.retryAfter ||
          errorData?.payload?.remainingSeconds ||
          errorData?.remainingSeconds ||
          error?.retryAfter ||
          error?.payload?.remainingSeconds ||
          error?.remainingSeconds;
        const suggestion = errorData?.suggestion || error?.suggestion;
        const alternativeMethod =
          suggestion?.alternativeMethod || errorData?.alternativeMethod || error?.alternativeMethod;

        const errorWithRetry = {
          ...error,
          ...errorData,
          code: errorData?.code || error?.code,
          retryAfter,
          suggestion,
          alternativeMethod,
        };

        const errorCode = errorData?.code || error?.code;
        const isRecaptchaError = errorCode === 400660 || errorCode === 400661;
        if (isRecaptchaError) {
          showToast({ title: t(`errors:${errorCode}`), description: t('errors:errorSigningInDescription') });
          return;
        }

        // Check for merged account
        const wasMerged = errorData?.wasMerged || error?.wasMerged;
        if (wasMerged) {
          const mergedUser = errorData?.user || error?.user;
          const redirectMethod = errorData?.redirectMethod || error?.redirectMethod;
          if (mergedUser && redirectMethod) {
            setMergedAccountInfo({ redirectMethod, user: mergedUser });
            setSendingCode(false);
            return;
          }
        }

        // Check for bridge verification opportunity (email not found for new user)
        if (authMethod === 'email' && (errorCode === 404 || errorData?.userNotFound || error?.userNotFound)) {
          setBridgeEmail(email);
          setBridgeStep('ask');
          setSendingCode(false);
          return;
        }

        const authMethodRedirect = errorData?.authMethodRedirect || error?.authMethodRedirect;
        if (errorCode === 400650 && authMethodRedirect) {
          const { redirectTo, phone: redirectPhone, email: redirectEmail } = authMethodRedirect;

          if (redirectTo === 'phone' && redirectPhone) {
            const formattedPhone = formatPhoneForMask(redirectPhone);
            setValue('phone', formattedPhone);
            switchAuthMethod('sms');

            showToast({
              title: t('wrongAuthMethod'),
              description: t('accountRegisteredWithPhone'),
            });
          } else if (redirectTo === 'email' && redirectEmail) {
            setValue('email', redirectEmail);
            switchAuthMethod('email');

            showToast({
              title: t('wrongAuthMethod'),
              description: t('accountRegisteredWithEmail'),
            });
          } else if (redirectTo === 'whatsapp' && redirectPhone) {
            const formattedPhone = formatPhoneForMask(redirectPhone);
            setValue('phone', formattedPhone);
            switchAuthMethod('whatsapp');

            showToast({
              title: t('wrongAuthMethod'),
              description: t('accountRegisteredWithWhatsApp'),
            });
          }

          setSendingCode(false);
          return;
        }

        if (authMethod === 'sms' && errorCode === 242424) {
          try {
            const response = await handleSignIn(`+${clearPhone(`+${DDI}${phone}`)}`);
            if (response?.token?.idToken) {
              await handleSuccessfulAuthentication('sms', response?.user);
            } else if (response?.user?.id || response?.notConfirmed) {
              setPinStep(true);
              const sessionValue = isValidSession(response?.session) ? response.session : '';
              if (sessionValue) {
                setSessionStorageWithoutParse('mfa_session', sessionValue);
              }
              setPendingAuth({
                method: 'sms',
                identifier: `+${clearPhone(`+${DDI}${phone}`)}`,
                session: sessionValue,
              });
              setCounter(60);
            } else {
              await handleSuccessfulAuthentication('sms', response?.user);
            }
          } catch (error: any) {
            if (error?.code === 400671) {
              setPinStep(true);
              setPendingAuth({ method: 'sms', identifier: `+${clearPhone(`+${DDI}${phone}`)}`, session: '' });
              setCounter(60);
            } else {
              handleAuthenticationError('sms', errorWithRetry);
            }
          }
        } else if (authMethod === 'sms' && errorCode === 400671) {
          setPinStep(true);
          setPendingAuth({ method: 'sms', identifier: `+${clearPhone(`+${DDI}${phone}`)}`, session: '' });
          setCounter(60);
        } else {
          handleAuthenticationError(authMethod, errorWithRetry);
        }
      } finally {
        setSendingCode(false);
      }
    } else {
      setValidatingCode(true);
      try {
        if (authMethod === 'sms') {
          if (!isMFALogin && !getSessionStorageWithoutParse('mfa_session')) {
            const user = await handleSignUpConfirmation(`+${clearPhone(`+${DDI}${phone}`)}`, code);
            await handleSuccessfulAuthentication('sms', user);
          } else {
            const response = await handleConfirmSignIn(
              `+${clearPhone(`+${DDI}${phone}`)}`,
              code,
              getSessionStorageWithoutParse('mfa_session') || ''
            );
            if (response?.token?.idToken || !response?.user?.id) {
              await handleSuccessfulAuthentication('sms', response?.user);
            } else {
              await handleSignInResponse(response);
            }
          }
        } else if (authMethod === 'email') {
          // Email code verification
          const response = await verifyEmailCode(email, code, bridgeBypass || undefined);
          if (response.token?.idToken) {
            await handleSuccessfulAuthentication('email', response.user);
          } else {
            handleAuthenticationError('email', { message: t('authenticationFailed') });
          }
        } else if (authMethod === 'whatsapp') {
          // WhatsApp code verification
          const fullPhone = `+${clearPhone(`+${DDI}${phone}`)}`;
          const response = await verifyWhatsAppCode(fullPhone, code);
          if (response.token) {
            await handleSuccessfulAuthentication('whatsapp', response.user);
          } else {
            handleAuthenticationError('whatsapp', { message: t('authenticationFailed') });
          }
        } else if (authMethod === 'dependent_code') {
          // Dependent code verification
          const response = await verifyDependentCode(dependentCodeInput, code);
          if (response.token) {
            await handleSuccessfulAuthentication('dependent_code', response.user);
          } else {
            showToast({ title: t('authenticationFailed'), description: t('tryAgainLater'), variant: 'error' });
          }
        }
      } catch (error: any) {
        const errorCode = error?.code || error?.error?.code;
        if (authMethod === 'email' && errorCode === 400909) {
          clearPendingAuth();
          setPinStep(false);
          setCode('');
          setBridgeStep('ask');
          return;
        }

        const retryAfter = error?.retryAfter || error?.payload?.remainingSeconds || error?.remainingSeconds;
        const suggestion = error?.suggestion;
        const alternativeMethod = suggestion?.alternativeMethod || error?.alternativeMethod;

        const errorWithRetry = {
          ...error,
          retryAfter,
          suggestion,
          alternativeMethod,
        };

        console.error('Code verification error:', error);
        handleAuthenticationError(authMethod, errorWithRetry);
      } finally {
        setValidatingCode(false);
      }
    }
  };

  const resendCode = async () => {
    try {
      setSendingCode(true);

      if (authMethod === 'sms') {
        const response = await resendSignInCodeV2(`+${clearPhone(`+${DDI}${phone}`)}`);

        const sessionValue = isValidSession(response?.session) ? response.session : '';
        if (sessionValue) {
          setSessionStorageWithoutParse('mfa_session', sessionValue);
        }
        setPendingAuth({ method: 'sms', identifier: `+${clearPhone(`+${DDI}${phone}`)}`, session: sessionValue });
      } else if (authMethod === 'email') {
        try {
          const response = await sendEmailVerificationCode(email);
          if (!response.success) {
            handleAuthenticationError('email', { message: response.message });
            setSendingCode(false);
            return;
          }
        } catch (error: any) {
          if (error?.code === 400650 && error?.authMethodRedirect) {
            const { redirectTo, phone: redirectPhone, email: redirectEmail } = error.authMethodRedirect;

            if (redirectTo === 'phone' && redirectPhone) {
              const formattedPhone = formatPhoneForMask(redirectPhone);
              setValue('phone', formattedPhone);
              switchAuthMethod('sms');
              showToast({
                title: t('wrongAuthMethod'),
                description: t('accountRegisteredWithPhone'),
              });
            } else if (redirectTo === 'email' && redirectEmail) {
              setValue('email', redirectEmail);
              switchAuthMethod('email');
              showToast({
                title: t('wrongAuthMethod'),
                description: t('accountRegisteredWithEmail'),
              });
            } else if (redirectTo === 'whatsapp' && redirectPhone) {
              const formattedPhone = formatPhoneForMask(redirectPhone);
              setValue('phone', formattedPhone);
              switchAuthMethod('whatsapp');
              showToast({
                title: t('wrongAuthMethod'),
                description: t('accountRegisteredWithWhatsApp'),
              });
            }

            setSendingCode(false);
            return;
          }

          const retryAfter = error?.retryAfter || error?.payload?.remainingSeconds || error?.remainingSeconds;
          const suggestion = error?.suggestion;
          const alternativeMethod = suggestion?.alternativeMethod || error?.alternativeMethod;

          const errorWithRetry = {
            ...error,
            retryAfter,
            suggestion,
            alternativeMethod,
          };

          handleAuthenticationError('email', errorWithRetry);
          setSendingCode(false);
          return;
        }
      } else if (authMethod === 'whatsapp') {
        try {
          const fullPhone = `+${clearPhone(`+${DDI}${phone}`)}`;
          await resendWhatsAppCode(fullPhone);
        } catch (error: any) {
          if (error?.code === 400650 && error?.authMethodRedirect) {
            const { redirectTo, phone: redirectPhone, email: redirectEmail } = error.authMethodRedirect;

            if (redirectTo === 'phone' && redirectPhone) {
              const formattedPhone = formatPhoneForMask(redirectPhone);
              setValue('phone', formattedPhone);
              switchAuthMethod('sms');
              showToast({
                title: t('wrongAuthMethod'),
                description: t('accountRegisteredWithPhone'),
              });
            } else if (redirectTo === 'email' && redirectEmail) {
              setValue('email', redirectEmail);
              switchAuthMethod('email');
              showToast({
                title: t('wrongAuthMethod'),
                description: t('accountRegisteredWithEmail'),
              });
            }

            setSendingCode(false);
            return;
          }

          const retryAfter = error?.retryAfter || error?.payload?.remainingSeconds || error?.remainingSeconds;
          const suggestion = error?.suggestion;
          const alternativeMethod = suggestion?.alternativeMethod || error?.alternativeMethod;

          const errorWithRetry = {
            ...error,
            retryAfter,
            suggestion,
            alternativeMethod,
          };

          handleAuthenticationError('whatsapp', errorWithRetry);
          setSendingCode(false);
          return;
        }
      } else if (authMethod === 'dependent_code') {
        try {
          const response = await sendDependentCode(dependentCodeInput.trim());
          if (response?.sentToEmail) {
            setDependentSentToEmail(response.sentToEmail);
          }
        } catch (error: any) {
          showToast({
            title: t('dependentCodeInvalid'),
            description: error?.message || t('tryAgainLater'),
            variant: 'error',
          });
          setSendingCode(false);
          return;
        }
      }

      // Refresh do timestamp do pendingAuth no resend (preserva method/identifier/session)
      const currentPending = getPendingAuth();
      if (currentPending) {
        setPendingAuth({
          method: currentPending.method,
          identifier: currentPending.identifier,
          session: currentPending.session,
        });
      }

      setCounter(60);
      setSendingCode(false);

      showToast({
        title: t('codeSent'),
        description: t('checkYourInbox'),
        variant: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      setSendingCode(false);

      const retryAfter = error?.retryAfter || error?.payload?.remainingSeconds || error?.remainingSeconds;
      const suggestion = error?.suggestion;
      const alternativeMethod = suggestion?.alternativeMethod || error?.alternativeMethod;

      const errorWithRetry = {
        ...error,
        retryAfter,
        suggestion,
        alternativeMethod,
      };

      if (error?.code === 400650 && error?.authMethodRedirect) {
        const { redirectTo, phone: redirectPhone, email: redirectEmail } = error.authMethodRedirect;

        if (redirectTo === 'phone' && redirectPhone) {
          const formattedPhone = formatPhoneForMask(redirectPhone);
          setValue('phone', formattedPhone);
          switchAuthMethod('sms');
          showToast({
            title: t('wrongAuthMethod'),
            description: t('accountRegisteredWithPhone'),
          });
        } else if (redirectTo === 'email' && redirectEmail) {
          setValue('email', redirectEmail);
          switchAuthMethod('email');
          showToast({
            title: t('wrongAuthMethod'),
            description: t('accountRegisteredWithEmail'),
          });
        } else if (redirectTo === 'whatsapp' && redirectPhone) {
          const formattedPhone = formatPhoneForMask(redirectPhone);
          setValue('phone', formattedPhone);
          switchAuthMethod('whatsapp');
          showToast({
            title: t('wrongAuthMethod'),
            description: t('accountRegisteredWithWhatsApp'),
          });
        }
        return;
      }

      handleAuthenticationError(authMethod, errorWithRetry);
      console.log(error);
    }
  };

  // Bridge Verification handlers
  const handleBridgeSendCode = async () => {
    const fullPhone = `+${clearPhone(`+${DDI}${phone}`)}`;
    if (!isValidPhone(fullPhone)) {
      showToast({ title: t('errors:errorTitle'), description: t('errors:invalidPhone'), variant: 'error' });
      return;
    }

    setBridgeSending(true);
    try {
      const response = await sendBridgeVerification(bridgeEmail, fullPhone, bridgeVerificationMethod);

      if (response.existingUserFound && response.codeSent) {
        setBridgeToken(response.bridgeToken);
        setBridgeMaskedPhone(response.maskedPhone);
        setBridgeStep('code-verification');
        setBridgeCounter(60);
      } else {
        showToast({
          title: t('bridgeNoAccount'),
          variant: 'info',
          duration: 4000,
        });
        // No account found — proceed with normal email account creation
        // bypassBridgeCheck: true tells the backend to allow new user creation
        setBridgeBypass(true);
        setBridgeStep('idle');
        setPinStep(true);
      }
    } catch (error: any) {
      const errorCode = error?.code || error?.error?.code;
      if (errorCode) {
        showToast({ title: t('errors:errorTitle'), description: t(`errors:${errorCode}`), variant: 'error' });
      } else {
        showToast({ title: t('errors:errorTitle'), description: error?.message, variant: 'error' });
      }
    } finally {
      setBridgeSending(false);
    }
  };

  const handleBridgeConfirmCode = async () => {
    if (!bridgeToken || bridgeCode.length < 6) return;

    setBridgeSending(true);
    try {
      const response = await confirmBridgeVerification(bridgeToken, bridgeCode);

      if (response.linked && response.user) {
        showToast({ title: t('bridgeSuccess'), variant: 'success' });
        await handleSuccessfulAuthentication('email', response.user);
      } else {
        showToast({
          title: t('errors:errorTitle'),
          description: t('authenticationFailed'),
          variant: 'error',
        });
      }
    } catch (error: any) {
      const errorCode = error?.code || error?.error?.code;
      if (errorCode) {
        showToast({ title: t('errors:errorTitle'), description: t(`errors:${errorCode}`), variant: 'error' });
      } else {
        showToast({ title: t('errors:errorTitle'), description: error?.message, variant: 'error' });
      }
    } finally {
      setBridgeSending(false);
    }
  };

  const handleBridgeResendCode = async () => {
    const fullPhone = `+${clearPhone(`+${DDI}${phone}`)}`;
    setBridgeSending(true);
    try {
      const response = await sendBridgeVerification(bridgeEmail, fullPhone, bridgeVerificationMethod);
      if (response.existingUserFound && response.codeSent) {
        setBridgeToken(response.bridgeToken);
        setBridgeMaskedPhone(response.maskedPhone);
        setBridgeCounter(60);
        showToast({ title: t('codeSent'), description: t('checkYourInbox'), variant: 'success', duration: 3000 });
      }
    } catch (error: any) {
      showToast({ title: t('errors:errorTitle'), description: error?.message, variant: 'error' });
    } finally {
      setBridgeSending(false);
    }
  };

  // Handle merged account redirect
  const handleMergedRedirect = () => {
    if (!mergedAccountInfo) return;
    const { redirectMethod, user } = mergedAccountInfo;

    if (redirectMethod === 'phone' || redirectMethod === 'sms') {
      if (user.phone) {
        const formattedPhone = formatPhoneForMask(user.phone);
        setValue('phone', formattedPhone);
      }
      switchAuthMethod('sms');
    } else if (redirectMethod === 'whatsapp') {
      if (user.phone) {
        const formattedPhone = formatPhoneForMask(user.phone);
        setValue('phone', formattedPhone);
      }
      switchAuthMethod('whatsapp');
    } else if (redirectMethod === 'email') {
      if (user.email) {
        setValue('email', user.email);
      }
      switchAuthMethod('email');
    }

    setMergedAccountInfo(null);
  };

  const switchAuthMethod = (method: AuthMethod) => {
    if (method === 'sms' && !smsLogin) {
      return;
    }
    // Don't allow switching to email if feature flag is disabled
    if (method === 'email' && !loginWithEmail) {
      return;
    }
    // Don't allow switching to whatsapp if feature flag is disabled
    if (method === 'whatsapp' && !whatsappLogin) {
      return;
    }

    if (method !== authMethod) {
      setAuthMethod(method);

      if (!pinStep) {
        setCode('');
        setCounter(0);
      }

      setSendingCode(false);
      setValidatingCode(false);
      setBridgeBypass(false);
      setBridgeStep('idle');

      setAuthErrors((prev) => ({ ...prev, [authMethod]: undefined }));
    }
  };

  const getAuthMethodIcon = (method: AuthMethod) => {
    switch (method) {
      case 'sms':
        return <Smartphone className='h-4 w-4' />;
      case 'email':
        return <Mail className='h-4 w-4' />;
      case 'whatsapp':
        return <WhatsAppIcon className='h-4 w-4' />;
    }
  };

  const getAuthMethodLabel = (method: AuthMethod) => {
    switch (method) {
      case 'sms':
        return t('smsAuth');
      case 'email':
        return t('emailAuth');
      case 'whatsapp':
        return t('whatsappAuth');
    }
  };

  const getSubtitle = () => {
    switch (authMethod) {
      case 'sms':
        return t('subtitle');
      case 'email':
        return t('emailSubtitle');
      case 'whatsapp':
        return t('whatsappSubtitle');
      case 'dependent_code':
        return t('dependentCodeSubtitle');
    }
  };

  const getInsertCodeText = () => {
    switch (authMethod) {
      case 'sms':
        return t('insertCode');
      case 'email':
        return t('insertEmailCode');
      case 'whatsapp':
        return t('insertWhatsappCode');
      case 'dependent_code':
        return t('insertDependentCode');
    }
  };

  const getSendingCodeText = () => {
    switch (authMethod) {
      case 'sms':
        return t('sendingCode');
      case 'email':
        return t('sendingEmailCode');
      case 'whatsapp':
        return t('sendingWhatsappCode');
      case 'dependent_code':
        return t('sendingDependentCode');
    }
  };

  return (
    <Modal
      isOpen={loginOpen}
      onOpenChange={(open) => {
        if (!open) clearPendingAuth();
        setLoginOpen(open);
      }}
      dismissOnOverlayClick={!pinStep}>
      <form className='relative flex flex-col gap-6 p-2' onSubmit={handleSubmit(confirm)}>
        {/* Loading overlay - busca dados do usuário */}
        {loadingUserData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='absolute inset-0 z-50 flex flex-col items-center justify-center rounded-lg bg-card'>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className='mb-4 h-8 w-8 rounded-full border-2 border-border border-t-foreground'
            />
            <p className='text-sm text-muted-foreground'>{t('loadingUserData')}</p>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.12 }}
          className={cn('flex gap-3', !pinStep ? 'items-start' : 'items-center')}>
          <div className='mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center bg-primary/10'>
            <Ticket className='h-5 w-5 text-primary' />
          </div>
          <div className='flex-1'>
            <h2 className='text-xl font-medium'>{t('title')}</h2>
            <AnimatePresence mode='wait'>
              {!pinStep && (
                <motion.p
                  key='subtitle'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                  className='text-sm text-gray2'>
                  {getSubtitle()}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Auth Method Selector - Minimalista */}
        <AnimatePresence mode='wait'>
          {!pinStep && bridgeStep === 'idle' && authMethod !== 'dependent_code' && (
            <motion.div
              key='auth-methods'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className='flex border-b border-border'>
              {availableAuthMethods.map((method) => (
                <button
                  key={method}
                  type='button'
                  onClick={() => switchAuthMethod(method)}
                  disabled={sendingCode}
                  className={`relative flex flex-1 items-center justify-center gap-2 py-3 text-sm transition-colors disabled:opacity-50 ${
                    authMethod === method ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  {getAuthMethodIcon(method)}
                  <span>{getAuthMethodLabel(method)}</span>
                  {authMethod === method && (
                    <motion.div
                      layoutId='auth-indicator'
                      className='absolute bottom-0 left-0 right-0 h-0.5 bg-zinc-900'
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error banners */}
        {authErrors[authMethod] && !pinStep && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className='rounded-lg border border-red-200 bg-red-50 p-4'>
            <div className='flex items-start gap-3'>
              <div className='text-lg text-red-600'>❌</div>
              <div className='flex-1'>
                <p className='text-sm font-medium text-red-800'>{authErrors[authMethod]}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* SMS fallback suggestion */}
        {(smsFailedSuggestion || authErrors.sms) &&
          !pinStep &&
          authMethod === 'sms' &&
          (loginWithEmail || whatsappLogin) && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
              <div className='flex items-start gap-3'>
                <div className='text-lg text-amber-600'>⚠️</div>
                <div className='flex-1'>
                  <p className='mb-2 text-sm font-medium text-amber-800'>{t('smsNotWorking')}</p>
                  <div className='flex gap-2'>
                    {whatsappLogin && (
                      <Button
                        type='button'
                        variant='outline'
                        className='h-8 border-amber-300 px-3 py-1 text-sm text-amber-700 hover:bg-amber-100'
                        onClick={() => switchAuthMethod('whatsapp')}
                        disabled={sendingCode || validatingCode}>
                        <WhatsAppIcon className='mr-1 h-3 w-3' /> {t('tryWhatsappAuth')}
                      </Button>
                    )}
                    {loginWithEmail && (
                      <Button
                        type='button'
                        variant='outline'
                        className='h-8 border-amber-300 px-3 py-1 text-sm text-amber-700 hover:bg-amber-100'
                        onClick={() => switchAuthMethod('email')}
                        disabled={sendingCode || validatingCode}>
                        <Mail className='mr-1 h-3 w-3' /> {t('tryEmailAuth')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

        {/* Email fallback suggestion when email fails */}
        {authErrors.email && !pinStep && authMethod === 'email' && (smsLogin || whatsappLogin) && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
            <div className='flex items-start gap-3'>
              <div className='text-lg text-blue-600'>💡</div>
              <div className='flex-1'>
                <p className='mb-2 text-sm font-medium text-blue-800'>{t('emailNotWorking')}</p>
                <div className='flex gap-2'>
                  {smsLogin && (
                    <Button
                      type='button'
                      variant='outline'
                      className='h-8 border-blue-300 px-3 py-1 text-sm text-blue-700 hover:bg-blue-100'
                      onClick={() => switchAuthMethod('sms')}
                      disabled={sendingCode || validatingCode}>
                      <Smartphone className='mr-1 h-3 w-3' /> {t('trySmsAuth')}
                    </Button>
                  )}
                  {whatsappLogin && (
                    <Button
                      type='button'
                      variant='outline'
                      className='h-8 border-blue-300 px-3 py-1 text-sm text-blue-700 hover:bg-blue-100'
                      onClick={() => switchAuthMethod('whatsapp')}
                      disabled={sendingCode || validatingCode}>
                      <WhatsAppIcon className='mr-1 h-3 w-3' /> {t('tryWhatsappAuth')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* WhatsApp fallback suggestion when whatsapp fails */}
        {authErrors.whatsapp && !pinStep && authMethod === 'whatsapp' && (smsLogin || loginWithEmail) && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className='rounded-lg border border-green-200 bg-green-50 p-4'>
            <div className='flex items-start gap-3'>
              <div className='text-lg text-green-600'>💡</div>
              <div className='flex-1'>
                <p className='mb-2 text-sm font-medium text-green-800'>{t('whatsappNotWorking')}</p>
                <div className='flex gap-2'>
                  {smsLogin && (
                    <Button
                      type='button'
                      variant='outline'
                      className='h-8 border-green-300 px-3 py-1 text-sm text-green-700 hover:bg-green-100'
                      onClick={() => switchAuthMethod('sms')}
                      disabled={sendingCode || validatingCode}>
                      <Smartphone className='mr-1 h-3 w-3' /> {t('trySmsAuth')}
                    </Button>
                  )}
                  {loginWithEmail && (
                    <Button
                      type='button'
                      variant='outline'
                      className='h-8 border-green-300 px-3 py-1 text-sm text-green-700 hover:bg-green-100'
                      onClick={() => switchAuthMethod('email')}
                      disabled={sendingCode || validatingCode}>
                      <Mail className='mr-1 h-3 w-3' /> {t('tryEmailAuth')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Merged Account Redirect Screen */}
        {mergedAccountInfo && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className='flex flex-col items-center space-y-5 py-6'>
            <div className='flex h-16 w-16 items-center justify-center rounded-full bg-amber-100'>
              <GitMerge className='h-8 w-8 text-amber-600' />
            </div>
            <div className='space-y-2 text-center'>
              <h3 className='text-lg font-semibold text-zinc-900'>{t('mergedAccountTitle')}</h3>
              <p className='text-sm text-zinc-500'>
                {t('mergedAccountMessage', {
                  method:
                    mergedAccountInfo.redirectMethod === 'phone' || mergedAccountInfo.redirectMethod === 'sms'
                      ? t('mergedAccountPhone')
                      : mergedAccountInfo.redirectMethod === 'email'
                        ? t('mergedAccountEmail')
                        : t('mergedAccountWhatsapp'),
                })}
              </p>
            </div>
            <div className='flex w-full gap-2'>
              <Button type='button' variant='outline' className='flex-1' onClick={() => setMergedAccountInfo(null)}>
                {t('cancel')}
              </Button>
              <Button type='button' className='flex-1 border border-black' onClick={handleMergedRedirect}>
                {t('mergedAccountRedirect', {
                  method:
                    mergedAccountInfo.redirectMethod === 'phone' || mergedAccountInfo.redirectMethod === 'sms'
                      ? t('mergedAccountPhone')
                      : mergedAccountInfo.redirectMethod === 'email'
                        ? t('mergedAccountEmail')
                        : t('mergedAccountWhatsapp'),
                })}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Bridge Verification Screens */}
        {bridgeStep !== 'idle' && !mergedAccountInfo && (
          <AnimatePresence mode='wait'>
            {bridgeStep === 'ask' && (
              <motion.div
                key='bridge-ask'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: smoothEase }}
                className='flex flex-col items-center space-y-5 py-4'>
                <div className='flex h-16 w-16 items-center justify-center rounded-full bg-blue-100'>
                  <Smartphone className='h-8 w-8 text-blue-600' />
                </div>
                <div className='space-y-2 text-center'>
                  <h3 className='text-lg font-semibold text-zinc-900'>{t('bridgeTitle')}</h3>
                  <p className='text-sm text-zinc-500'>{t('bridgeSubtitle')}</p>
                </div>
                <div className='flex w-full flex-col gap-2'>
                  <Button
                    type='button'
                    className='w-full border border-black'
                    onClick={() => setBridgeStep('phone-entry')}>
                    {t('bridgeYes')}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    className='w-full'
                    onClick={() => {
                      setBridgeBypass(true);
                      setBridgeStep('idle');
                      setPinStep(true);
                    }}>
                    {t('bridgeNo')}
                  </Button>
                </div>
              </motion.div>
            )}

            {bridgeStep === 'phone-entry' && (
              <motion.div
                key='bridge-phone'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: smoothEase }}
                className='flex flex-col gap-4'>
                <button
                  type='button'
                  onClick={() => setBridgeStep('ask')}
                  className='flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700'>
                  <ArrowLeft className='h-4 w-4' /> {t('bridgeCancel')}
                </button>
                <p className='text-sm text-zinc-600'>{t('bridgePhonePrompt')}</p>

                {/* Country selector */}
                <div className='flex w-full flex-col gap-1.5'>
                  <label className='text-sm font-medium text-zinc-700'>{t('country')}</label>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type='button'
                        variant='outline'
                        className='h-11 w-full justify-between border-gray2 bg-transparent px-3 font-normal text-zinc-900 hover:border-zinc-400 hover:bg-transparent hover:text-zinc-900'>
                        <span>{`${COUNTRY} ${DDI}`}</span>
                        <ChevronDown className='h-4 w-4 text-zinc-400' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='p-0' align='start' side='bottom'>
                      <Command value={String(selectedCountry)}>
                        <CommandInput placeholder={t('search')} />
                        <CommandList>
                          <CommandEmpty>{t('notFound')}</CommandEmpty>
                          <CommandGroup>
                            {COUNTRIES.map(({ mobileCode, name }) => {
                              if (mobileCode.length !== 1) {
                                return (
                                  <CommandItem
                                    className='cursor-pointer'
                                    key={`bridge-${name[lng]}-${mobileCode}`}
                                    value={name[lng]}
                                    onSelect={(value: string) => {
                                      setSelectedCountry(
                                        COUNTRIES.findIndex(
                                          ({ name }) => (name[lng] as string).toLowerCase() === value.toLowerCase()
                                        )
                                      );
                                      setOpen(false);
                                    }}>
                                    {name[lng]} {mobileCode}
                                  </CommandItem>
                                );
                              }
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Phone input */}
                <div className='flex w-full flex-col gap-1.5'>
                  <label className='text-sm font-medium text-zinc-700'>{t('phone')}</label>
                  <Controller
                    control={control}
                    name='phone'
                    render={({ field }) => {
                      if (DDI === '+55') {
                        return (
                          <MaskInput
                            autoComplete='off'
                            placeholder='(11) 99999-9999'
                            mask='(99) 99999-9999'
                            type='tel'
                            className='border-gray2 transition-all duration-200 focus-visible:border-zinc-700 focus-visible:shadow-sm'
                            {...field}
                          />
                        );
                      }
                      return (
                        <Input
                          autoComplete='off'
                          placeholder='(11) 99999-9999'
                          type='tel'
                          className='border-gray2 transition-all duration-200 focus-visible:border-zinc-700 focus-visible:shadow-sm'
                          value={field.value}
                          onChange={(event) => {
                            if (!Number(event.target.value) || event.target.value === '') {
                              return field.onChange('');
                            }
                            return field.onChange(event.target.value);
                          }}
                        />
                      );
                    }}
                  />
                </div>

                {/* Verification method selector */}
                <div className='flex w-full flex-col gap-1.5'>
                  <label className='text-sm font-medium text-zinc-700'>{t('bridgeSelectMethod')}</label>
                  <div className='flex gap-2'>
                    {whatsappLogin && (
                      <button
                        type='button'
                        onClick={() => setBridgeVerificationMethod('whatsapp')}
                        className={cn(
                          'flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                          bridgeVerificationMethod === 'whatsapp'
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-200 text-zinc-600 hover:border-zinc-400'
                        )}>
                        <WhatsAppIcon className='h-4 w-4' /> WhatsApp
                      </button>
                    )}
                    {smsLogin && (
                      <button
                        type='button'
                        onClick={() => setBridgeVerificationMethod('sms')}
                        className={cn(
                          'flex flex-1 items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                          bridgeVerificationMethod === 'sms'
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-200 text-zinc-600 hover:border-zinc-400'
                        )}>
                        <Smartphone className='h-4 w-4' /> SMS
                      </button>
                    )}
                  </div>
                </div>

                <Button
                  type='button'
                  className='w-full border border-black'
                  isLoading={bridgeSending}
                  disabled={bridgeSending || !phone || phone.includes('_')}
                  onClick={handleBridgeSendCode}>
                  {t('bridgeVerify')}
                </Button>
              </motion.div>
            )}

            {bridgeStep === 'code-verification' && (
              <motion.div
                key='bridge-code'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: smoothEase }}
                className='flex flex-col items-center space-y-5 py-4'>
                <button
                  type='button'
                  onClick={() => setBridgeStep('phone-entry')}
                  className='flex w-full items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700'>
                  <ArrowLeft className='h-4 w-4' /> {t('bridgeCancel')}
                </button>

                <div className='flex h-16 w-16 items-center justify-center'>
                  {bridgeVerificationMethod === 'whatsapp' ? (
                    <WhatsAppIcon className='h-8 w-8 text-zinc-700' />
                  ) : (
                    <Smartphone className='h-8 w-8 text-zinc-700' />
                  )}
                </div>

                <div className='space-y-2 text-center'>
                  <p className='text-sm text-zinc-500'>
                    {t('bridgeCodeSent', { maskedPhone: bridgeMaskedPhone || '' })}
                  </p>
                </div>

                <div className='flex w-full justify-center py-2'>
                  <VerificationInput onChange={(c) => setBridgeCode(c)} />
                </div>

                {/* Resend */}
                <div className='flex items-center justify-center text-sm'>
                  {bridgeSending ? (
                    <div className='flex items-center gap-2 text-zinc-400'>
                      <PulsingDots />
                      <span>{t('bridgeVerifying')}</span>
                    </div>
                  ) : bridgeCounter > 0 ? (
                    <span className='text-zinc-400'>{t('resendIn', { counter: formatSeconds(bridgeCounter) })}</span>
                  ) : (
                    <button
                      type='button'
                      className='text-zinc-600 underline transition-colors hover:text-zinc-900'
                      onClick={handleBridgeResendCode}>
                      {t('resendCode')}
                    </button>
                  )}
                </div>

                <Button
                  type='button'
                  className='w-full border border-black'
                  isLoading={bridgeSending}
                  disabled={bridgeSending || bridgeCode.length < 6}
                  onClick={handleBridgeConfirmCode}>
                  {t('validate')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Main Content - Animated transitions between steps */}
        {bridgeStep === 'idle' && !mergedAccountInfo && (
          <AnimatePresence mode='wait'>
            {pinStep ? (
              <motion.div
                key='pin-step'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: smoothEase }}
                className='flex flex-col items-center space-y-5 py-4'>
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.12, ease: smoothEase }}
                  className='flex h-16 w-16 items-center justify-center'>
                  {authMethod === 'sms' ? (
                    <Smartphone className='h-8 w-8 text-zinc-700' />
                  ) : authMethod === 'email' || authMethod === 'dependent_code' ? (
                    <Mail className='h-8 w-8 text-zinc-700' />
                  ) : (
                    <WhatsAppIcon className='h-8 w-8 text-zinc-700' />
                  )}
                </motion.div>

                {/* Text */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12, delay: 0.03 }}
                  className='space-y-2 text-center'>
                  <p className='text-sm text-zinc-500'>{getInsertCodeText()}</p>
                  <p className='text-lg font-semibold text-zinc-900'>
                    {authMethod === 'email' ? email : replaceWithAsterisks(phone, 5, 5)}
                  </p>
                </motion.div>

                {/* Verification Input */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12, delay: 0.05 }}
                  className='flex w-full justify-center py-2'>
                  <VerificationInput onChange={(c) => setCode(c)} />
                </motion.div>

                {/* Resend Code */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12, delay: 0.07 }}
                  className='flex items-center justify-center text-sm'>
                  {sendingCode ? (
                    <div className='flex w-full items-center justify-center gap-2 text-zinc-400'>
                      <PulsingDots />
                      <span>{getSendingCodeText()}</span>
                    </div>
                  ) : counter ? (
                    <span className='text-zinc-400'>{t('resendIn', { counter: formatSeconds(counter) })}</span>
                  ) : (
                    <button
                      type='button'
                      className='text-zinc-600 underline transition-colors hover:text-zinc-900'
                      onClick={resendCode}
                      disabled={sendingCode}>
                      {t('resendCode')}
                    </button>
                  )}
                </motion.div>

                {/* Try Another Method - More Evident Section */}
                {availableAuthMethods.length > 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.12, delay: 0.1 }}
                    className='w-full border-t border-zinc-100 pt-4'>
                    <p className='mb-3 text-center text-sm text-zinc-500'>{t('didntReceiveCode')}</p>
                    <div className='flex justify-center gap-2'>
                      {availableAuthMethods
                        .filter((method) => method !== authMethod)
                        .map((method) => (
                          <button
                            key={method}
                            type='button'
                            onClick={() => {
                              clearPendingAuth();
                              setPinStep(false);
                              switchAuthMethod(method);
                            }}
                            disabled={sendingCode || validatingCode}
                            className='flex w-full items-center justify-center gap-2 border border-zinc-200 px-4 py-2 text-sm text-zinc-600 transition-colors hover:border-zinc-400 hover:text-zinc-900 disabled:opacity-40'>
                            {getAuthMethodIcon(method)}
                            {getAuthMethodLabel(method)}
                          </button>
                        ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ) : authMethod === 'sms' || authMethod === 'whatsapp' ? (
              <motion.div
                key='phone-step'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: smoothEase }}
                className='flex flex-col gap-4'>
                {/* Country */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12 }}
                  className='flex w-full flex-col gap-1.5'>
                  <label className='text-sm font-medium text-zinc-700'>{t('country')}</label>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type='button'
                        variant='outline'
                        className='h-11 w-full justify-between border-gray2 bg-transparent px-3 font-normal text-zinc-900 hover:border-zinc-400 hover:bg-transparent hover:text-zinc-900'>
                        <span>{`${COUNTRY} ${DDI}`}</span>
                        <ChevronDown className='h-4 w-4 text-zinc-400' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='p-0' align='start' side='bottom'>
                      <Command value={String(selectedCountry)}>
                        <CommandInput placeholder={t('search')} />
                        <CommandList>
                          <CommandEmpty>{t('notFound')}</CommandEmpty>
                          <CommandGroup>
                            {COUNTRIES.map(({ mobileCode, name }) => {
                              if (mobileCode.length !== 1) {
                                return (
                                  <CommandItem
                                    className='cursor-pointer'
                                    key={`${name[lng]}-${mobileCode}`}
                                    value={name[lng]}
                                    onSelect={(value: string) => {
                                      setSelectedCountry(
                                        COUNTRIES.findIndex(
                                          ({ name }) => (name[lng] as string).toLowerCase() === value.toLowerCase()
                                        )
                                      );
                                      setOpen(false);
                                    }}>
                                    {name[lng]} {mobileCode}
                                  </CommandItem>
                                );
                              }
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </motion.div>

                {/* Phone */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12, delay: 0.03 }}
                  className='flex w-full flex-col gap-1.5'>
                  <label className='text-sm font-medium text-zinc-700'>{t('phone')}</label>
                  <Controller
                    control={control}
                    name='phone'
                    render={({ field }) => {
                      if (DDI === '+55') {
                        return (
                          <MaskInput
                            autoComplete='off'
                            placeholder='(11) 99999-9999'
                            mask='(99) 99999-9999'
                            type='tel'
                            className='border-gray2 transition-all duration-200 focus-visible:border-zinc-700 focus-visible:shadow-sm'
                            {...field}
                          />
                        );
                      }
                      return (
                        <Input
                          autoComplete='off'
                          placeholder='(11) 99999-9999'
                          type='tel'
                          className='border-gray2 transition-all duration-200 focus-visible:border-zinc-700 focus-visible:shadow-sm'
                          value={field.value}
                          onChange={(event) => {
                            if (!Number(event.target.value) || event.target.value === '') {
                              return field.onChange('');
                            }
                            return field.onChange(event.target.value);
                          }}
                        />
                      );
                    }}
                  />
                  <AnimatePresence>
                    {isForeignDDI(DDI) && loginWithEmail && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className='text-sm text-amber-600'>
                        {t('foreignPhoneUseEmail')}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            ) : authMethod === 'dependent_code' ? (
              <motion.div
                key='dependent-step'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: smoothEase }}
                className='flex flex-col gap-4'>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12 }}
                  className='flex w-full flex-col gap-1.5'>
                  <label className='text-sm font-medium text-zinc-700'>{t('dependentCodeLabel')}</label>
                  <Input
                    autoComplete='off'
                    placeholder={t('dependentCodePlaceholder')}
                    type='text'
                    className='h-11 border-gray2 transition-all duration-200 focus-visible:border-zinc-700 focus-visible:shadow-sm'
                    value={dependentCodeInput}
                    onChange={(e) => setDependentCodeInput(e.target.value)}
                  />
                </motion.div>
                <button
                  type='button'
                  className='text-left text-sm text-zinc-500 underline hover:text-zinc-900'
                  onClick={() => {
                    setIsDependentMode(false);
                    setAuthMethod(whatsappLogin ? 'whatsapp' : smsLogin ? 'sms' : 'email');
                    setDependentCodeInput('');
                  }}>
                  {t('backToNormalLogin')}
                </button>
              </motion.div>
            ) : (
              <motion.div
                key='email-step'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: smoothEase }}
                className='flex flex-col gap-4'>
                {/* Email */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12 }}
                  className='flex w-full flex-col gap-1.5'>
                  <label className='text-sm font-medium text-zinc-700'>{t('email')}</label>
                  <Controller
                    control={control}
                    name='email'
                    render={({ field }) => (
                      <Input
                        autoComplete='email'
                        placeholder={t('emailPlaceholder')}
                        type='email'
                        className='h-11 border-gray2 transition-all duration-200 focus-visible:border-zinc-700 focus-visible:shadow-sm'
                        {...field}
                      />
                    )}
                  />
                  <AnimatePresence>
                    {email && !isValidEmail(email) && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className='text-sm text-red-600'>
                        {t('pleaseEnterValidEmail')}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {authenticateUsers === 'v2' &&
          !pinStep &&
          bridgeStep === 'idle' &&
          !mergedAccountInfo &&
          authMethod !== 'dependent_code' && <GoogleReCaptchaV2 action='LOGIN' />}

        {/* Footer buttons - hidden during bridge/merged screens */}
        {bridgeStep === 'idle' && !mergedAccountInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.12, delay: 0.05 }}
            className='flex flex-col gap-3'>
            <div className='flex items-center justify-end gap-2'>
              <Button
                className='w-32'
                onClick={() => {
                  clearPendingAuth();
                  setLoginOpen(false);
                }}
                type='reset'
                variant='outline'>
                {t('cancel')}
              </Button>
              <Button
                isLoading={sendingCode || validatingCode}
                className='w-32 border border-foreground'
                variant='default'
                type='submit'
                disabled={
                  sendingCode ||
                  validatingCode ||
                  (pinStep
                    ? code?.length < 6
                    : authMethod === 'dependent_code'
                      ? !dependentCodeInput.trim()
                      : authMethod === 'sms' || authMethod === 'whatsapp'
                        ? !phone || phone.includes('_')
                        : !email || !isValidEmail(email))
                }>
                {pinStep ? t('validate') : t('continue')}
              </Button>
            </div>

            {/* Dependent login link */}
            {!pinStep && authMethod !== 'dependent_code' && (
              <button
                type='button'
                className='text-center text-sm text-zinc-500 underline hover:text-zinc-900'
                onClick={() => {
                  setIsDependentMode(true);
                  setAuthMethod('dependent_code');
                }}>
                {t('dependentLogin')}
              </button>
            )}
          </motion.div>
        )}
      </form>
    </Modal>
  );
};
